/**
 * <size-unit-toggle> — switches footwear size LABELS between US men's and US women's.
 *
 * Labels only. The radio inputs behind each size keep their original Shopify option
 * value, so the variant added to cart is never affected by this control.
 *
 * The active unit lives on <html data-size-unit>, hardcoded to "men" in theme.liquid so
 * every page loads showing the product's real option values. CSS decides which of the
 * two spans is visible; this component only flips the attribute and keeps every toggle
 * on the page in sync.
 *
 * The choice is deliberately NOT persisted. Men's is the actual variant value, so a
 * remembered "women" would mean a shopper lands on a product already reading converted
 * labels they never asked for on that page - and could believe they are ordering a size
 * that isn't what the product lists.
 */
(function () {
const STORAGE_KEY = 'insport:size-unit';
const UNITS = ['men', 'women'];

/**
 * Mirrors the shopper's chosen women's size onto the add-to-cart form as a line item
 * property, so the cart, checkout, confirmation email and packing slip all show it
 * alongside the men's variant title instead of contradicting the page.
 *
 * The value is read from the label the shopper is looking at — Liquid already rendered
 * the converted size there — so there is no second copy of the conversion logic to
 * drift. The input stays disabled (unsubmitted) in men's view, leaving normal orders
 * untouched.
 */
function syncCartProperty() {
  const input = document.querySelector('[data-size-unit-property]');
  if (!input) return;

  const checked = document.querySelector(
    '.variant-input-wrapper[data-option-slug="size"] input[type="radio"]:checked'
  );
  const label = checked
    ? document.querySelector(`label[for="${CSS.escape(checked.id)}"]`)
    : null;
  const womens = label ? label.querySelector('[data-size-women]') : null;
  const mens = label ? label.querySelector('[data-size-men]') : null;

  // A size with no published women's equivalent renders the men's label plus a
  // superscript marker. Recording that as a "women's size" would be a lie.
  const isRealConversion = womens && !womens.querySelector('.size-unit-toggle__mark');

  if (document.documentElement.dataset.sizeUnit === 'women' && isRealConversion) {
    const womensText = womens.textContent.trim();
    const mensText = mens ? mens.textContent.trim() : '';
    // Naming the listed size inside the value ties this line to the variant title
    // printed directly above it. Without it the shopper sees two different sizes
    // ("S" and "10") with nothing saying they are the same garment.
    input.value = mensText ? `${womensText} (listed as ${mensText})` : womensText;
    input.disabled = false;
  } else {
    input.value = '';
    input.disabled = true;
  }
}

class SizeUnitToggle extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-unit]'));
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onExternalChange = this.onExternalChange.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    window.addEventListener('size-unit:change', this.onExternalChange);

    this.sync(SizeUnitToggle.current());
    // Runs on every section re-render too — the theme replaces this element (and the
    // cart form) whenever the variant changes, which resets the property input.
    syncCartProperty();
  }

  disconnectedCallback() {
    window.removeEventListener('size-unit:change', this.onExternalChange);
  }

  static current() {
    const unit = document.documentElement.dataset.sizeUnit;
    return UNITS.includes(unit) ? unit : 'men';
  }

  onClick(event) {
    const tab = event.target.closest('[data-unit]');
    if (tab) this.setUnit(tab.dataset.unit);
  }

  // Left/right arrows move between tabs, per the tablist pattern.
  onKeydown(event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const next = SizeUnitToggle.current() === 'men' ? 'women' : 'men';
    this.setUnit(next);
    const tab = this.tabs.find((t) => t.dataset.unit === next);
    if (tab) tab.focus();
  }

  setUnit(unit) {
    if (!UNITS.includes(unit)) return;
    document.documentElement.dataset.sizeUnit = unit;
    try {
      // sessionStorage, not localStorage: carries the choice while they browse and
      // clears with the tab. theme.liquid re-applies it before first paint.
      sessionStorage.setItem(STORAGE_KEY, unit);
    } catch (e) {
      // Private browsing or storage disabled — the choice just won't carry.
    }
    window.dispatchEvent(new CustomEvent('size-unit:change', { detail: unit }));
    syncCartProperty();
  }

  onExternalChange(event) {
    this.sync(event.detail);
  }

  sync(unit) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.unit === unit;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
    });
  }
}

// Picking a different size has to update the recorded women's size. Delegated from the
// document so it survives the theme re-rendering the picker, and deferred a tick so it
// reads the state after the theme's own change handler has settled.
document.addEventListener('change', (event) => {
  if (event.target.closest('.variant-input-wrapper[data-option-slug="size"]')) {
    setTimeout(syncCartProperty, 0);
  }
});

if (!customElements.get('size-unit-toggle')) {
  customElements.define('size-unit-toggle', SizeUnitToggle);
}
// Wrapped in an IIFE so these names never reach global scope. Several apps inject
// scripts on this store, and a clashing top-level `const` would throw and take the
// whole toggle down with it.
})();
